/**
 * Shared drawer animation utilities for Asteno theme
 * Used by mobile menu drawer and product search drawer
 */

class DrawerAnimations {
  /**
   * Initialize a drawer with GSAP animations
   * @param {Object} options - Configuration options
   * @param {string} options.drawerId - ID of the drawer element
   * @param {string} options.contentSelector - Selector for the content container
   * @param {string} options.overlaySelector - Selector for the overlay element
   * @param {string} options.closeSelector - Selector for the close button
   * @param {string} options.openTriggerSelector - Selector for elements that open the drawer
   * @param {string} options.direction - Direction of the drawer ('left' or 'right')
   * @param {Array} options.itemSelectors - Optional selectors for items to animate (for staggered entrances)
   * @param {Function} options.onOpen - Optional callback when drawer opens
   * @param {Function} options.onClose - Optional callback when drawer closes
   */
  constructor(options) {
    this.options = Object.assign({
      direction: 'right',
      itemSelectors: [], 
      onOpen: () => {},
      onClose: () => {}
    }, options);

    this.drawer = document.getElementById(this.options.drawerId);
    if (!this.drawer) {
      console.error(`Drawer element with ID "${this.options.drawerId}" not found`);
      return;
    }

    this.content = this.drawer.querySelector(this.options.contentSelector);
    this.overlay = this.drawer.querySelector(this.options.overlaySelector);
    this.closeButton = this.drawer.querySelector(this.options.closeSelector);
    this.isOpen = false;
    this.timeline = null;
    
    // Get items for staggered animation if provided
    this.animatedItems = [];
    if (this.options.itemSelectors && this.options.itemSelectors.length > 0) {
      this.options.itemSelectors.forEach(selector => {
        const items = this.drawer.querySelectorAll(selector);
        if (items.length > 0) {
          this.animatedItems.push(...items);
        }
      });
    }
    
    this.initialize();
  }

  /**
   * Initialize the drawer animations and event listeners
   */
  initialize() {
    // Create the GSAP timeline
    this.createTimeline();

    // Set initial states
    this.setInitialState();

    // Add event listeners
    this.setupEventListeners();
  }

  /**
   * Create the GSAP timeline for the drawer
   */
  createTimeline() {
    const startPos = this.options.direction === 'left' ? '-100%' : '100%';
    
    this.timeline = gsap.timeline({
      paused: true,
      onReverseComplete: () => {
        this.drawer.classList.remove('active');
        document.documentElement.classList.remove('ap-lockall');
        
        // Reset initial states after reverse animation
        this.setInitialState();
        
        // Callback
        if (typeof this.options.onClose === 'function') {
          this.options.onClose();
        }
      }
    });

    // Base animation sequence
    this.timeline.to(this.drawer, {
      visibility: 'visible',
      duration: 0
    })
    .to(this.overlay, {
      opacity: 1,
      duration: 0.3
    })
    .to(this.content, {
      x: 0,
      duration: 0.3,
      ease: 'power2.out'
    }, '-=0.2');
    
    // Add staggered animations for items if they exist
    if (this.animatedItems.length > 0) {
      this.timeline.from(this.animatedItems, {
        x: this.options.direction === 'left' ? -40 : 40,
        opacity: 0,
        duration: 0.3,
        stagger: 0.05,
        ease: 'power2.out'
      }, '-=0.1');
    }
  }

  /**
   * Set the initial state of the drawer elements
   */
  setInitialState() {
    const startPos = this.options.direction === 'left' ? '-100%' : '100%';
    
    gsap.set(this.drawer, { visibility: 'hidden' });
    gsap.set(this.content, { x: startPos });
    gsap.set(this.overlay, { opacity: 0 });
    
    if (this.animatedItems.length > 0) {
      gsap.set(this.animatedItems, { 
        x: this.options.direction === 'left' ? -40 : 40, 
        opacity: 0 
      });
    }
  }

  /**
   * Set up event listeners for the drawer
   */
  setupEventListeners() {
    // Event listeners for open triggers
    document.querySelectorAll(this.options.openTriggerSelector).forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    // Close button listener
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Overlay click listener
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key listener
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  /**
   * Open the drawer
   */
  open() {
    if (!this.isOpen) {
      this.drawer.classList.add('active');
      document.documentElement.classList.add('ap-lockall');
      this.timeline.play();
      this.isOpen = true;
      
      // Callback
      if (typeof this.options.onOpen === 'function') {
        this.options.onOpen();
      }
    }
  }

  /**
   * Close the drawer
   */
  close() {
    if (this.isOpen) {
      // Play the reverse animation at 1.5x speed for snappier closing
      this.timeline.timeScale(1.5).reverse();
      this.isOpen = false;
    }
  }

  /**
   * Toggle the drawer state
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

// Export for global use
window.DrawerAnimations = DrawerAnimations; 